import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DiagnosticSnapshot, DiagnosticStatus, normalizeDiagnosticSnapshot } from '../types';
import { fetchDiagnosticSnapshot, fetchDiagnosticStatus, queueDiagnostics } from '../services/api';
import { analyzeLog, ISSUE_WINDOWS, IssueWindow, issueContextLines, LogAnalysis } from '../utils/logSeverity';
import '../styles/Diagnostics.css';

const DEFAULT_ISSUE_WINDOW: IssueWindow = 'week';

const analyzeSnapshot = (snapshot: DiagnosticSnapshot, window: IssueWindow): LogAnalysis[] => {
    const reference = new Date(snapshot.collectedAtUtc).getTime();
    return snapshot.logs.map((log) => analyzeLog(log.content, window, reference));
};

const pickDefaultLog = (snapshot: DiagnosticSnapshot): number => {
    const analyses = analyzeSnapshot(snapshot, DEFAULT_ISSUE_WINDOW);
    const withErrors = snapshot.logs.findIndex((log, index) => log.status === 'collected' && analyses[index].errorCount > 0);
    if (withErrors >= 0) return withErrors;
    return Math.max(0, snapshot.logs.findIndex((log) => log.status === 'collected'));
};

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

const errorMessage = (error: unknown): string => error instanceof Error ? error.message : String(error);

const MINIMUM_SYSTEMREPORTER_VERSION = '1.0.73';

const STATUS_LABELS: Record<string, string> = {
    pending: 'Waiting for device', running: 'Collecting logs',
    succeeded: 'Completed', failed: 'Failed', cancelled: 'Cancelled',
};

const formatDate = (value?: string | null): string => value ? new Date(value).toLocaleString() : '-';

const formatBytes = (bytes?: number | null): string => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const shortVersion = (version: string): string => version.split('+')[0];

const StatusBadge: React.FC<{ status: string; label?: string }> = ({ status, label }) => (
    <span className={`diagnostics-badge diagnostics-badge-${status.replace(/[^a-z]/gi, '').toLowerCase()}`}>
        {label ?? status}
    </span>
);

const Diagnostics: React.FC<{
    serialNumber: string;
    systemReporterVersion?: string;
    canRetrieveLogs: boolean;
}> = ({ serialNumber, systemReporterVersion, canRetrieveLogs }) => {
    const [status, setStatus] = useState<DiagnosticStatus | null>(null);
    const [snapshot, setSnapshot] = useState<DiagnosticSnapshot | null>(null);
    const [selectedLog, setSelectedLog] = useState(0);
    const [error, setError] = useState('');
    const [snapshotError, setSnapshotError] = useState('');
    const [loading, setLoading] = useState(true);
    const [queueing, setQueueing] = useState(false);
    const [reload, setReload] = useState(0);
    const [issueWindow, setIssueWindow] = useState<IssueWindow>(DEFAULT_ISSUE_WINDOW);
    const [onlyIssues, setOnlyIssues] = useState(false);
    const [currentIssue, setCurrentIssue] = useState(-1);
    const logRef = useRef<HTMLPreElement>(null);

    useEffect(() => {
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout>;
        const load = async () => {
            try {
                const next = await fetchDiagnosticStatus(serialNumber, controller.signal);
                if (controller.signal.aborted) return;
                setStatus(next);
                setError('');
                if (next.command?.status === 'pending' || next.command?.status === 'running') {
                    timer = setTimeout(load, 5000);
                }
            } catch (failure) {
                if (!controller.signal.aborted) setError(`Unable to load diagnostics: ${errorMessage(failure)}`);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        void load();
        return () => {
            controller.abort();
            clearTimeout(timer);
        };
    }, [serialNumber, reload]);

    const snapshotId = status?.snapshotCommandId;
    useEffect(() => {
        setSnapshot(null);
        setSnapshotError('');
        setSelectedLog(0);
        if (!snapshotId) return;
        const controller = new AbortController();
        void fetchDiagnosticSnapshot(serialNumber, snapshotId, controller.signal)
            .then((result) => {
                if (controller.signal.aborted) return;
                const normalizedSnapshot = normalizeDiagnosticSnapshot(result);
                if (!normalizedSnapshot) {
                    throw new Error('The server returned a log snapshot in an unsupported format.');
                }
                setSnapshot(normalizedSnapshot);
                setSelectedLog(pickDefaultLog(normalizedSnapshot));
            })
            .catch((failure) => {
                if (!controller.signal.aborted) setSnapshotError(`Unable to load log snapshot: ${errorMessage(failure)}`);
            });
        return () => controller.abort();
    }, [serialNumber, snapshotId, reload]);

    const requestLogs = async () => {
        setQueueing(true);
        setError('');
        try {
            await queueDiagnostics(serialNumber);
            setReload((value) => value + 1);
        } catch (failure) {
            setError(`Unable to request logs: ${errorMessage(failure)}`);
        } finally {
            setQueueing(false);
        }
    };

    const download = () => {
        if (!snapshot) return;
        const url = URL.createObjectURL(new Blob([JSON.stringify({
            serialNumber, commandId: snapshotId, ...snapshot,
        }, null, 2)], { type: 'application/json' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `diagnostics-${serialNumber.replace(/[^a-zA-Z0-9_-]/g, '_')}.json`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    };

    const command = status?.command;
    const busy = command?.status === 'pending' || command?.status === 'running';
    const selected = snapshot?.logs[selectedLog];
    const analyses = useMemo(() => snapshot ? analyzeSnapshot(snapshot, issueWindow) : [], [snapshot, issueWindow]);
    const selectedAnalysis = analyses[selectedLog];
    const issueLines = useMemo(() => selectedAnalysis?.issueLines ?? [], [selectedAnalysis]);
    const filesWithErrors = analyses.filter((analysis) => analysis.errorCount > 0).length;
    const visibleLines = useMemo(
        () => selectedAnalysis && onlyIssues ? issueContextLines(selectedAnalysis) : null,
        [selectedAnalysis, onlyIssues],
    );

    useEffect(() => setCurrentIssue(-1), [selectedLog, issueWindow, snapshot]);

    useEffect(() => {
        if (currentIssue < 0) return;
        const element = logRef.current?.querySelector(`[data-line="${issueLines[currentIssue]}"]`);
        (element as HTMLElement | null)?.scrollIntoView?.({ block: 'center' });
    }, [currentIssue, issueLines, onlyIssues]);

    const goToIssue = (step: 1 | -1) => {
        if (!issueLines.length) return;
        setCurrentIssue((value) => {
            if (value < 0) return step === 1 ? 0 : issueLines.length - 1;
            return (value + step + issueLines.length) % issueLines.length;
        });
    };

    const indexedLogs = (snapshot?.logs ?? []).map((log, index) => ({ log, index }));
    const foundLogs = indexedLogs.filter(({ log }) => log.status !== 'missing');
    const missingLogs = indexedLogs.filter(({ log }) => log.status === 'missing');
    const collectedCount = indexedLogs.filter(({ log }) => log.status === 'collected').length;
    const sources = Array.from(new Set(foundLogs.map(({ log }) => log.source)));

    const renderIssueCounts = (analysis: LogAnalysis | undefined) => {
        if (!analysis) return null;
        return <>
            {analysis.errorCount > 0 && <span className="diagnostics-count diagnostics-count-error" title={plural(analysis.errorCount, 'error')}>
                {analysis.errorCount}
            </span>}
            {analysis.warningCount > 0 && <span className="diagnostics-count diagnostics-count-warning" title={plural(analysis.warningCount, 'warning')}>
                {analysis.warningCount}
            </span>}
            {analysis.errorCount === 0 && analysis.warningCount === 0 && <span className="diagnostics-count diagnostics-count-ok" title="No issues found">
                {'\u2713'}
            </span>}
        </>;
    };

    const renderLogButton = (
        { log, index }: { log: DiagnosticSnapshot['logs'][number]; index: number },
        showSource = false,
    ) => {
        const analysis = analyses[index];
        const hasErrors = (analysis?.errorCount ?? 0) > 0;
        return (
            <li key={`${log.source}-${log.fileName}`}>
                <button type="button"
                    className={`diagnostics-file${index === selectedLog ? ' selected' : ''}${hasErrors ? ' has-errors' : ''}`}
                    aria-pressed={index === selectedLog}
                    aria-label={`${log.source} / ${log.fileName} - ${log.status}`}
                    onClick={() => setSelectedLog(index)}>
                    <span className="diagnostics-file-name" title={`${log.source} / ${log.fileName}`}>
                        {showSource ? `${log.source} / ${log.fileName}` : log.fileName}
                    </span>
                    <span className="diagnostics-file-badges">
                        {log.status === 'collected'
                            ? <>
                                {renderIssueCounts(analysis)}
                                {log.truncated && <StatusBadge status="limit" label="limited" />}
                            </>
                            : <StatusBadge status={log.status} />}
                    </span>
                </button>
            </li>
        );
    };

    const renderLogLine = (lineIndex: number, position: number) => {
        if (lineIndex < 0) return <span key={`gap-${position}`} className="diagnostics-line diagnostics-line-gap">...</span>;
        const line = selectedAnalysis.lines[lineIndex];
        const classes = ['diagnostics-line'];
        if (line.severity) classes.push(`diagnostics-line-${line.severity}`);
        if (line.severity && !line.inWindow) classes.push('diagnostics-line-older');
        if (currentIssue >= 0 && issueLines[currentIssue] === lineIndex) classes.push('diagnostics-line-current');
        return <span key={lineIndex} data-line={lineIndex} className={classes.join(' ')}>{line.text}</span>;
    };

    return (
        <section className="diagnostics-panel" aria-label="Device diagnostics">
            <div className="diagnostics-toolbar">
                <div className="diagnostics-request" role="status">
                    {loading && <span>Loading diagnostics...</span>}
                    {!loading && !command && <span>No diagnostic request yet.</span>}
                    {command && <>
                        <StatusBadge status={command.status} label={STATUS_LABELS[command.status] ?? command.status} />
                        <span className="diagnostics-muted">Requested {formatDate(command.createdAtUtc)}</span>
                    </>}
                </div>
                <div className="diagnostics-actions">
                    <button type="button" className="secondary-button" disabled={queueing}
                        onClick={() => setReload((value) => value + 1)}>Refresh status</button>
                    {snapshot && <button type="button" className="secondary-button" onClick={download}>Download snapshot</button>}
                    <button type="button" className="primary-button"
                        disabled={loading || queueing || busy || !canRetrieveLogs}
                        title={!canRetrieveLogs ? `Requires SystemReporter ${MINIMUM_SYSTEMREPORTER_VERSION} or newer` : undefined}
                        onClick={() => void requestLogs()}>
                        {queueing ? 'Queueing...' : 'Retrieve logs'}
                    </button>
                </div>
            </div>

            {!canRetrieveLogs && <p className="diagnostics-callout diagnostics-callout-warning" role="status">
                Retrieve logs is disabled until SystemReporter {MINIMUM_SYSTEMREPORTER_VERSION} or newer has run on the device
                {systemReporterVersion ? ` (current: ${systemReporterVersion})` : ' (current version: unknown)'}.
            </p>}
            {error && <p role="alert" className="diagnostics-callout diagnostics-callout-error">{error}</p>}
            {command?.status === 'pending' && <p className="diagnostics-callout diagnostics-callout-info">
                The request remains queued while the device is offline. An online agent normally checks for commands every 60 seconds.
            </p>}
            {command?.status === 'running' && <p className="diagnostics-callout diagnostics-callout-info">
                Waiting for the agent to upload and confirm the result. If this does not complete, check the agent version and service.
            </p>}
            {command?.output && <p className={`diagnostics-callout ${command.status === 'failed' ? 'diagnostics-callout-error' : ''}`}>{command.output}</p>}
            {snapshotError && <p role="alert" className="diagnostics-callout diagnostics-callout-error">{snapshotError}</p>}
            {snapshotId && !snapshot && !snapshotError && <p role="status" className="diagnostics-muted">Loading log snapshot...</p>}

            {snapshot && <>
                {snapshotId !== command?.id && <p className="diagnostics-callout diagnostics-callout-warning">
                    This is the previous snapshot, not the result of the latest request.
                </p>}
                <dl className="diagnostics-summary">
                    <div><dt>Snapshot collected</dt><dd>{formatDate(snapshot.collectedAtUtc)}</dd></div>
                    <div><dt>Agent</dt><dd title={snapshot.agentVersion}>{shortVersion(snapshot.agentVersion)}</dd></div>
                    <div><dt>Files collected</dt><dd>{collectedCount} / {snapshot.logs.length}</dd></div>
                    <div className={filesWithErrors > 0 ? 'diagnostics-summary-danger' : 'diagnostics-summary-healthy'}>
                        <dt>Files with errors</dt>
                        <dd title={`Errors in ${ISSUE_WINDOWS.find((option) => option.value === issueWindow)?.label.toLowerCase()}`}>
                            {filesWithErrors}
                        </dd>
                    </div>
                    <div><dt>SystemReporter</dt><dd>{systemReporterVersion || 'unknown'}</dd></div>
                </dl>

                <div className="diagnostics-viewer">
                    <nav className="diagnostics-files" aria-label="Log files">
                        {sources.map((source) => {
                            const sourceHasErrors = foundLogs.some(({ log, index }) => log.source === source && analyses[index]?.errorCount > 0);
                            return <div key={source} className="diagnostics-source">
                                <h5>
                                    {source}
                                    {sourceHasErrors && <span className="diagnostics-source-dot" title="Contains errors" aria-label="Contains errors" />}
                                </h5>
                                <ul>{foundLogs.filter(({ log }) => log.source === source).map((item) => renderLogButton(item))}</ul>
                            </div>;
                        })}
                        {foundLogs.length === 0 && <p className="diagnostics-muted">No log files were found.</p>}
                        {missingLogs.length > 0 && <details className="diagnostics-missing">
                            <summary>Not found ({missingLogs.length})</summary>
                            <p className="diagnostics-muted">Usually components or commands that have not been used yet.</p>
                            <ul>{missingLogs.map((item) => renderLogButton(item, true))}</ul>
                        </details>}
                    </nav>

                    <div className="diagnostics-content">
                        {selected ? <>
                            <div className="diagnostics-content-header">
                                <div>
                                    <span className="diagnostics-muted">{selected.source}</span>
                                    <h4>{selected.fileName}</h4>
                                </div>
                                <StatusBadge status={selected.status} />
                            </div>
                            <p className="diagnostics-meta">
                                <span>Size: {formatBytes(selected.originalBytes)}</span>
                                <span>Modified: {formatDate(selected.lastWriteTimeUtc)}</span>
                            </p>
                            {selected.truncated && <p className="diagnostics-callout diagnostics-callout-info">
                                Only the most recent part is included, or the collection limit was reached.
                            </p>}
                            {selected.error && <p role="alert" className="diagnostics-callout diagnostics-callout-error">{selected.error}</p>}
                            {selected.status === 'missing' && <p className="diagnostics-muted">This file does not exist on the device.</p>}
                            {selected.content != null && selectedAnalysis && <>
                                <div className="diagnostics-log-toolbar">
                                    <span className="diagnostics-issue-summary">
                                        <span className={selectedAnalysis.errorCount ? 'diagnostics-text-error' : ''}>
                                            {plural(selectedAnalysis.errorCount, 'error')}
                                        </span>
                                        <span className={selectedAnalysis.warningCount ? 'diagnostics-text-warning' : ''}>
                                            {plural(selectedAnalysis.warningCount, 'warning')}
                                        </span>
                                    </span>
                                    <label className="diagnostics-window">
                                        Count issues from
                                        <select value={issueWindow} onChange={(event) => setIssueWindow(event.target.value as IssueWindow)}>
                                            {ISSUE_WINDOWS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                        </select>
                                    </label>
                                    <label className="diagnostics-toggle">
                                        <input type="checkbox" checked={onlyIssues} disabled={!issueLines.length}
                                            onChange={(event) => setOnlyIssues(event.target.checked)} />
                                        Only errors &amp; warnings
                                    </label>
                                    <span className="diagnostics-issue-nav">
                                        <button type="button" className="secondary-button diagnostics-small-button" disabled={!issueLines.length}
                                            onClick={() => goToIssue(-1)}>Previous issue</button>
                                        <span className="diagnostics-muted diagnostics-issue-position">
                                            {!issueLines.length
                                                ? 'No issues'
                                                : currentIssue >= 0
                                                    ? `Issue ${currentIssue + 1} of ${issueLines.length}`
                                                    : plural(issueLines.length, 'issue')}
                                        </span>
                                        <button type="button" className="secondary-button diagnostics-small-button" disabled={!issueLines.length}
                                            onClick={() => goToIssue(1)}>Next issue</button>
                                    </span>
                                </div>
                                {selectedAnalysis.olderIssueCount > 0 && <p className="diagnostics-muted">
                                    {plural(selectedAnalysis.olderIssueCount, 'older issue')} outside the selected period
                                    {selectedAnalysis.olderIssueCount === 1 ? ' is' : ' are'} dimmed and not counted.
                                </p>}
                                <pre className="diagnostics-log" tabIndex={0} ref={logRef}>
                                    {selected.content === ''
                                        ? '(empty file)'
                                        : (visibleLines && visibleLines.length
                                            ? visibleLines
                                            : selectedAnalysis.lines.map((_, index) => index)).map(renderLogLine)}
                                </pre>
                            </>}
                        </> : <p className="diagnostics-muted">Select a log file.</p>}
                    </div>
                </div>
            </>}

            <p className="diagnostics-footnote">
                Requires SystemReporter {MINIMUM_SYSTEMREPORTER_VERSION} or newer. Logs stay on the device; this retrieves a limited snapshot
                that is available for 30 days. Common credentials are redacted automatically, but logs can still contain personal
                or sensitive information.
            </p>
        </section>
    );
};

export default Diagnostics;
